import React, { useState, useEffect } from 'react';
import { FaCog, FaGlobe, FaCreditCard, FaBell, FaShieldAlt, FaServer, FaTruck, FaHeadset, FaTools, FaSave, FaUndo, FaCheck, FaTimes } from 'react-icons/fa';
import useSettings from '../hooks/useSettings';
import { useToast } from '../components/ToastContainer';
import '../styles/SystemSettings.css';

const SystemSettings = () => {
  const {
    settings,
    categories,
    loading,
    error,
    saveLoading,
    saveSettings,
    resetSettings,
    updateSetting,
    getSetting
  } = useSettings();
  
  const { showToast } = useToast();
  const [activeCategory, setActiveCategory] = useState('general');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({});

  // Suivre les changements
  useEffect(() => {
    if (Object.keys(settings).length > 0 && Object.keys(originalSettings).length === 0) {
      setOriginalSettings({...settings});
    }
  }, [settings, originalSettings]);

  // Détecter les changements non sauvegardés
  useEffect(() => {
    const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasUnsavedChanges(hasChanges);
  }, [settings, originalSettings]);

  // Icônes pour les catégories
  const getCategoryIcon = (iconName) => {
    const icons = {
      'cog': FaCog,
      'globe': FaGlobe,
      'credit-card': FaCreditCard,
      'bell': FaBell,
      'shield': FaShieldAlt,
      'server': FaServer,
      'truck': FaTruck,
      'headset': FaHeadset,
      'tools': FaTools
    };
    const IconComponent = icons[iconName] || FaCog;
    return <IconComponent />;
  };

  // Sauvegarder les paramètres
  const handleSave = async () => {
    try {
      await saveSettings(settings);
      setOriginalSettings({...settings});
      showToast('Paramètres sauvegardés avec succès', 'success');
    } catch (error) {
      showToast('Erreur lors de la sauvegarde', 'error');
    }
  };

  // Annuler les modifications
  const handleCancel = () => {
    updateSetting(null, originalSettings); // Reset all settings
    setHasUnsavedChanges(false);
    showToast('Modifications annulées', 'info');
  };

  // Réinitialiser une catégorie
  const handleResetCategory = async (category) => {
    if (window.confirm(`Êtes-vous sûr de vouloir réinitialiser les paramètres de la catégorie "${category}" ?`)) {
      try {
        await resetSettings(category);
        showToast(`Paramètres de la catégorie "${category}" réinitialisés`, 'success');
      } catch (error) {
        showToast('Erreur lors de la réinitialisation', 'error');
      }
    }
  };

  // Render des différents types de champs
  const renderField = (key, value, type = 'text') => {
    switch (type) {
      case 'boolean':
        return (
          <div className="toggle-switch">
            <input
              type="checkbox"
              id={key}
              checked={value || false}
              onChange={(e) => updateSetting(key, e.target.checked)}
            />
            <label htmlFor={key} className="toggle-label"></label>
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => updateSetting(key, parseInt(e.target.value))}
            className="settings-input"
          />
        );

      case 'select':
        const options = getSelectOptions(key);
        return (
          <select
            value={value || ''}
            onChange={(e) => updateSetting(key, e.target.value)}
            className="settings-select"
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'array':
        return (
          <textarea
            value={Array.isArray(value) ? value.join(', ') : ''}
            onChange={(e) => updateSetting(key, e.target.value.split(', ').filter(v => v.trim()))}
            className="settings-textarea"
            rows="3"
            placeholder="Séparez les valeurs par des virgules"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => updateSetting(key, e.target.value)}
            className="settings-input"
          />
        );
    }
  };

  // Options pour les champs select
  const getSelectOptions = (key) => {
    const optionsMap = {
      default_currency: [
        { value: 'EUR', label: 'Euro (EUR)' },
        { value: 'FCFA', label: 'Franc CFA (FCFA)' },
        { value: 'USD', label: 'Dollar US (USD)' }
      ],
      default_timezone: [
        { value: 'UTC', label: 'UTC' },
        { value: 'Europe/Paris', label: 'Europe/Paris' },
        { value: 'Africa/Dakar', label: 'Africa/Dakar' },
        { value: 'Africa/Abidjan', label: 'Africa/Abidjan' }
      ],
      theme: [
        { value: 'light', label: 'Clair' },
        { value: 'dark', label: 'Sombre' },
        { value: 'auto', label: 'Automatique' }
      ],
      date_format: [
        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
      ],
      time_format: [
        { value: '24h', label: '24 heures' },
        { value: '12h', label: '12 heures (AM/PM)' }
      ],
      backup_frequency: [
        { value: 'daily', label: 'Quotidien' },
        { value: 'weekly', label: 'Hebdomadaire' },
        { value: 'monthly', label: 'Mensuel' }
      ]
    };
    return optionsMap[key] || [];
  };

  // Obtenir le type de champ
  const getFieldType = (key) => {
    const typeMap = {
      // Boolean fields
      email_notifications: 'boolean',
      sms_notifications: 'boolean',
      push_notifications: 'boolean',
      notification_sound: 'boolean',
      password_require_uppercase: 'boolean',
      password_require_lowercase: 'boolean',
      password_require_numbers: 'boolean',
      password_require_symbols: 'boolean',
      two_factor_auth: 'boolean',
      auto_assign_missions: 'boolean',
      maintenance_mode: 'boolean',

      // Number fields
      default_commission_rate: 'number',
      min_commission_amount: 'number',
      max_commission_amount: 'number',
      session_timeout: 'number',
      password_min_length: 'number',
      max_file_upload_size: 'number',
      max_batch_operations: 'number',
      api_rate_limit: 'number',
      items_per_page: 'number',
      mission_timeout: 'number',
      max_mission_radius: 'number',
      support_ticket_auto_close: 'number',
      log_retention_days: 'number',

      // Select fields
      default_currency: 'select',
      default_timezone: 'select',
      theme: 'select',
      date_format: 'select',
      time_format: 'select',
      backup_frequency: 'select',

      // Array fields
      supported_languages: 'array',
      payment_methods: 'array',
      support_categories: 'array'
    };
    return typeMap[key] || 'text';
  };

  // Obtenir le label d'un champ
  const getFieldLabel = (key) => {
    const labelMap = {
      platform_name: 'Nom de la plateforme',
      platform_domain: 'Domaine de la plateforme',
      contact_email: 'Email de contact',
      contact_phone: 'Téléphone de contact',
      default_currency: 'Devise par défaut',
      default_timezone: 'Fuseau horaire par défaut',
      default_country: 'Pays par défaut',
      supported_languages: 'Langues supportées',
      default_language: 'Langue par défaut',
      default_commission_rate: 'Taux de commission (%)',
      min_commission_amount: 'Commission minimum',
      max_commission_amount: 'Commission maximum',
      payment_methods: 'Méthodes de paiement',
      email_notifications: 'Notifications email',
      sms_notifications: 'Notifications SMS',
      push_notifications: 'Notifications push',
      notification_sound: 'Son des notifications',
      session_timeout: 'Timeout de session (min)',
      password_min_length: 'Longueur min. mot de passe',
      password_require_uppercase: 'Majuscules requises',
      password_require_lowercase: 'Minuscules requises',
      password_require_numbers: 'Chiffres requis',
      password_require_symbols: 'Symboles requis',
      two_factor_auth: 'Authentification à deux facteurs',
      max_file_upload_size: 'Taille max fichier (MB)',
      max_batch_operations: 'Max opérations en lot',
      api_rate_limit: 'Limite API (req/min)',
      theme: 'Thème',
      items_per_page: 'Éléments par page',
      date_format: 'Format de date',
      time_format: 'Format d\'heure',
      auto_assign_missions: 'Auto-assignation missions',
      mission_timeout: 'Timeout mission (min)',
      max_mission_radius: 'Rayon max mission (km)',
      support_ticket_auto_close: 'Fermeture auto tickets (jours)',
      support_categories: 'Catégories de support',
      maintenance_mode: 'Mode maintenance',
      maintenance_message: 'Message de maintenance',
      backup_frequency: 'Fréquence de sauvegarde',
      log_retention_days: 'Rétention logs (jours)'
    };
    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading && Object.keys(settings).length === 0) {
    return (
      <div className="system-settings-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="system-settings-page">
        <div className="error-container">
          <p>Erreur: {error}</p>
          <button onClick={() => window.location.reload()}>Réessayer</button>
        </div>
      </div>
    );
  }

  const activeConfig = categories.find(cat => cat.id === activeCategory);

  return (
    <div className="system-settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="header-left">
          <FaCog className="page-icon" />
          <div>
            <h1>Paramètres Système</h1>
            <p>Configuration générale de la plateforme</p>
          </div>
        </div>
        <div className="header-actions">
          {hasUnsavedChanges && (
            <>
              <button className="btn-cancel" onClick={handleCancel}>
                <FaTimes />
                Annuler
              </button>
              <button className="btn-save" onClick={handleSave} disabled={saveLoading}>
                <FaSave />
                {saveLoading ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="settings-content">
        {/* Sidebar des catégories */}
        <aside className="settings-sidebar">
          <nav className="settings-nav">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`nav-item ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <div className="nav-icon">
                  {getCategoryIcon(category.icon)}
                </div>
                <div className="nav-content">
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                </div>
              </button>
            ))}
          </nav>
        </aside>

        {/* Contenu principal */}
        <main className="settings-main">
          {activeConfig && (
            <div className="settings-section">
              <div className="section-header">
                <div className="section-title">
                  <div className="section-icon">
                    {getCategoryIcon(activeConfig.icon)}
                  </div>
                  <div>
                    <h2>{activeConfig.name}</h2>
                    <p>{activeConfig.description}</p>
                  </div>
                </div>
                <button
                  className="btn-reset"
                  onClick={() => handleResetCategory(activeConfig.id)}
                >
                  <FaUndo />
                  Réinitialiser
                </button>
              </div>

              <div className="settings-form">
                {activeConfig.settings.map((settingKey) => (
                  <div key={settingKey} className="form-group">
                    <label className="form-label">
                      {getFieldLabel(settingKey)}
                    </label>
                    <div className="form-field">
                      {renderField(settingKey, getSetting(settingKey), getFieldType(settingKey))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Indicateur de changements non sauvegardés */}
      {hasUnsavedChanges && (
        <div className="unsaved-changes-indicator">
          <FaCheck className="indicator-icon" />
          <span>Vous avez des modifications non sauvegardées</span>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;